import SwapVertIcon from '@mui/icons-material/SwapVert';
import { Box, Divider, Typography } from '@mui/material';
import cn from 'classnames';
import * as React from 'react';
import { useCallback, useMemo } from 'react';

import { ReactComponent as DragControlIcon } from '../../assets/images/icon-drag-control.svg';
import { CAMPAIGN_ROW_HEIGHT_FULL } from '../../constants/campaigns';
import { useCurrency } from '../../contexts/CurrencyProvider';
import useWindowSize from '../../hooks/useWindowSize';
import { ICampaign, ICampaignItem, ICampaignSummary } from '../../types/campaigns';
import { IInventoryItem, IOnDemandItem } from '../../types/inventories';
import { IMarketplaceSendItem } from '../../types/marketplace';
import { InventorySidebarModeEnum, InventorySidebarTypeEnum } from '../../types/shell';
import { getSingleFulfillmentCenterId, hasMskuItem } from '../../utils/inventories';
import DraggableList from '../DraggableList/DraggableList';
import { ActionButton, RedCrossButton } from '../forms';
import { LabelWithEditButton } from '../forms/labels';
import { CampaignPreview, CampaignViewItem, FCDetails, InventoryViewItem } from '../index';
import MarketplaceSendItemView from '../MarketplaceSendItemView/MarketplaceSendItemView';

import styles from './InventorySidebar.module.scss';

const getCampaignId = (campaign: ICampaign) => campaign.box_id;

interface ICommonProps {
  className?: string;
  listClassName?: string;
  itemClassName?: string;
  label?: string | React.ReactNode;
  onSelect?: (item: ICampaignItem | IInventoryItem) => void;
  onEdit?: () => void;
  mode?: InventorySidebarModeEnum;
  showBorder?: boolean;
  showSeparators?: boolean;
  selected?: string;
  items?: (IInventoryItem | IOnDemandItem | ICampaign | ICampaignSummary)[] | null;
  marketplaceItems?: IMarketplaceSendItem[] | null;
  type: InventorySidebarTypeEnum;
  showCount?: boolean;
  showInStockCount?: boolean;
  showQuantity?: boolean;
  isEditable?: boolean;
  isDisabledFn?: (item: IInventoryItem | ICampaignItem) => boolean;
  reportMode?: boolean;
  onReorder?: (next: ICampaign[]) => void;
  onRemove?: (campaign: ICampaign) => void;
}

/* * * * * * * * * * * * * IMPORTANT NOTE ! * * * * * * * * * * * * */
/* If you need to add anything to this component, please, consider splitting it or making it less complicated */
/* Please, review to this ticket => https://bluebirdcx.atlassian.net/browse/EX-1165 */
const InventorySidebar: React.FC<ICommonProps> = ({
  className,
  listClassName,
  itemClassName,
  items,
  marketplaceItems,
  label,
  onSelect,
  onEdit,
  mode = InventorySidebarModeEnum.View,
  showBorder = true,
  showSeparators,
  selected,
  type,
  showCount = true,
  showInStockCount,
  showQuantity,
  isEditable,
  isDisabledFn,
  reportMode = false,
  onReorder,
  onRemove,
}) => {
  const { mobile } = useWindowSize();
  const { getCampaignTotal } = useCurrency();

  const isSelectMode = useMemo(() => mode === InventorySidebarModeEnum.Select, [mode]);

  const handleItemSelect = useCallback(
    (item: IInventoryItem | ICampaignItem) => {
      if (typeof onSelect === 'function') {
        onSelect(item);
      }
    },
    [onSelect],
  );

  const renderCampaignItem = useCallback(
    (item: ICampaign) => {
      const { box_id: boxId, name, icon_url: iconUrl, items: campaignItems } = item;
      const hasMsku = hasMskuItem(campaignItems);

      return (
        <div
          key={boxId}
          className={cn(styles.item, styles.campaign, {
            [styles.selectItem]: isSelectMode,
            [styles.selected]: selected === boxId,
          })}
        >
          <CampaignViewItem
            iconClassName={styles.campaignIcon}
            className={itemClassName}
            name={name}
            hasMsku={hasMsku}
            price={getCampaignTotal(item)}
            imageUrl={iconUrl}
            details={<FCDetails fulfillmentCenterId={getSingleFulfillmentCenterId(campaignItems)} />}
            hint={
              <CampaignPreview
                showPreview
                campaign={item}
                className={styles.preview}
                reportMode={reportMode}
                showWarning
              />
            }
          />
        </div>
      );
    },
    [isSelectMode, selected, itemClassName, reportMode, getCampaignTotal],
  );

  const renderInventoryItem = useCallback(
    (item: IInventoryItem | ICampaignItem) => {
      const { item_id: itemId } = item;
      const isDisabledInventoryItem = typeof isDisabledFn === 'function' ? isDisabledFn(item) : false;

      return (
        <div
          key={itemId}
          className={cn(styles.item, {
            [styles.selectItem]: isSelectMode,
            [styles.separated]: showSeparators,
            [styles.disabled]: isDisabledInventoryItem,
          })}
        >
          <InventoryViewItem
            item={item}
            showCount={showCount}
            showInStockCount={showInStockCount}
            showQuantity={showQuantity}
            className={itemClassName}
            actions={
              isSelectMode && (
                <ActionButton
                  title="Add"
                  onClick={() => handleItemSelect(item)}
                  className={styles.addBtn}
                  disabled={isDisabledInventoryItem}
                />
              )
            }
          />
        </div>
      );
    },
    [isSelectMode, handleItemSelect, showQuantity, showCount, showInStockCount, itemClassName],
  );

  const isCampaignType = useMemo(() => type === InventorySidebarTypeEnum.Campaign, [type]);
  const draggableCampaigns = useMemo(
    () => (isCampaignType ? (items as ICampaign[] | undefined) : undefined) ?? [],
    [isCampaignType, items],
  );
  const isReorderMode = useMemo(
    () => Boolean(onReorder) && draggableCampaigns.length >= 2,
    [onReorder, draggableCampaigns],
  );

  const renderMarketplaceItems = () =>
    marketplaceItems?.map((item) => (
      <div key={item.product_id} className={cn(styles.item, { [styles.separated]: showSeparators })}>
        <MarketplaceSendItemView item={item} showQuantity={showQuantity} className={itemClassName} />
      </div>
    ));

  const renderListContent = () => {
    if (isReorderMode && onReorder) {
      return (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary', mb: 1 }}>
            <SwapVertIcon fontSize="small" />
            <Typography variant="body2">
              <Box component="span" sx={{ fontWeight: 'medium', color: 'text.primary' }}>
                Drag to reorder.
              </Box>{' '}
              Top option shows first.
            </Typography>
          </Box>
          <Divider sx={{ mb: 2 }} />
          <Box sx={{ overflowX: 'hidden' }}>
            <DraggableList<ICampaign>
              items={draggableCampaigns}
              getId={getCampaignId}
              rowHeight={CAMPAIGN_ROW_HEIGHT_FULL}
              isBounded={false}
              onDragStop={onReorder}
              render={draggableCampaigns.map((campaign) => (
                <div key={campaign.box_id}>
                  <div className={styles.draggableCampaignRow}>
                    <DragControlIcon tabIndex={-1} className={styles.draggableCampaignHandle} />
                    <div className={styles.draggableCampaignBody}>{renderCampaignItem(campaign)}</div>
                    {onRemove && (
                      <RedCrossButton
                        onClick={() => onRemove(campaign)}
                        size="xs"
                        className={styles.draggableCampaignRemove}
                      />
                    )}
                  </div>
                </div>
              ))}
            />
          </Box>
          {renderMarketplaceItems()}
        </>
      );
    }
    return (
      <>
        {items?.map((el) =>
          isCampaignType ? renderCampaignItem(el as ICampaign) : renderInventoryItem(el as IInventoryItem),
        )}
        {renderMarketplaceItems()}
      </>
    );
  };

  return (
    <div className={cn(styles.inventory, { [styles.inventoryBorder]: !mobile && showBorder }, className)}>
      {label && (
        <span className={styles.inventoryLabel}>
          <LabelWithEditButton isEditable={isEditable} text={label} onEdit={onEdit} />
        </span>
      )}
      <div className={cn(styles.inventoryList, listClassName)}>{renderListContent()}</div>
    </div>
  );
};
export default InventorySidebar;
